/**
 * Google Ads recommendation types → the words a media planner reads.
 *
 * The performance analysis reviews Google's own recommendations and passes the
 * API enum through as `type` (RecommendationTypeEnum, e.g.
 * REFRESH_CUSTOMER_MATCH_LIST). That enum was the card headline, so the feed
 * read as a wall of shouted identifiers. This is the single place that turns
 * one into a sentence.
 *
 * Covers the types the backend maps to a lever
 * (campaign_optimizer recommendation → lever tables) plus the common ones Google
 * returns on Search / PMax / Demand Gen accounts. An unknown type is NOT shown
 * raw: it falls back to the enum sentence-cased ("Improve performance max ad
 * strength"), which is readable if not elegant — a new Google type must never
 * put a bare enum back on a card.
 *
 *   label        the headline for one recommendation
 *   many         the headline when several account-level ones are collapsed
 *                into one card; `{n}` is the count. Absent → "<label> (n)".
 *   maintenance  housekeeping Google raises routinely (list refreshes, ad
 *                strength nudges, auto-asset opt-ins). Still shown — only
 *                ranked below substantive cards.
 */
export const GOOGLE_RECOMMENDATION_TYPES = {
  // Audiences
  REFRESH_CUSTOMER_MATCH_LIST: {
    label: 'Refresh customer-match list',
    many: 'Refresh {n} customer-match lists',
    maintenance: true,
  },
  CUSTOM_AUDIENCE_OPT_IN: { label: 'Add a custom audience' },

  // Ad strength and creative
  IMPROVE_PERFORMANCE_MAX_AD_STRENGTH: {
    label: 'Improve Performance Max ad strength',
    many: 'Improve ad strength in {n} asset groups',
    maintenance: true,
  },
  IMPROVE_DEMAND_GEN_AD_STRENGTH: {
    label: 'Improve Demand Gen ad strength',
    many: 'Improve Demand Gen ad strength on {n} ads',
    maintenance: true,
  },
  RESPONSIVE_SEARCH_AD_IMPROVE_AD_STRENGTH: {
    label: 'Improve responsive search ad strength',
    many: 'Improve ad strength on {n} responsive search ads',
    maintenance: true,
  },
  RESPONSIVE_SEARCH_AD: { label: 'Add a responsive search ad', many: 'Add {n} responsive search ads' },
  RESPONSIVE_SEARCH_AD_ASSET: {
    label: 'Add headlines or descriptions to a responsive search ad',
    many: 'Add headlines or descriptions to {n} responsive search ads',
  },
  TEXT_AD: { label: 'Add a text ad', many: 'Add {n} text ads' },
  OPTIMIZE_AD_ROTATION: { label: 'Optimise ad rotation' },

  // Assets
  DYNAMIC_IMAGE_EXTENSION_OPT_IN: {
    label: 'Turn on dynamic image assets',
    maintenance: true,
  },
  SITELINK_ASSET: { label: 'Add sitelinks', many: 'Add sitelinks in {n} places' },
  CALLOUT_ASSET: { label: 'Add callouts', many: 'Add callouts in {n} places' },
  CALL_ASSET: { label: 'Add a call asset' },
  LEAD_FORM_ASSET: { label: 'Add a lead form asset' },

  // Keywords
  KEYWORD: { label: 'Add keywords' },
  KEYWORD_MATCH_TYPE: { label: 'Change keyword match types' },
  USE_BROAD_MATCH_KEYWORD: { label: 'Use broad match keywords' },

  // Bidding
  TARGET_CPA_OPT_IN: { label: 'Switch to Target CPA bidding' },
  TARGET_ROAS_OPT_IN: { label: 'Switch to Target ROAS bidding' },
  SET_TARGET_CPA: { label: 'Set a target CPA' },
  SET_TARGET_ROAS: { label: 'Set a target ROAS' },
  RAISE_TARGET_CPA: { label: 'Raise the target CPA' },
  RAISE_TARGET_CPA_BID_TOO_LOW: { label: 'Raise a target CPA that is too low' },
  LOWER_TARGET_ROAS: { label: 'Lower the target ROAS' },
  FORECASTING_SET_TARGET_CPA: { label: 'Set a target CPA (forecast)' },
  FORECASTING_SET_TARGET_ROAS: { label: 'Set a target ROAS (forecast)' },
  MAXIMIZE_CONVERSIONS_OPT_IN: { label: 'Switch to Maximise conversions bidding' },
  MAXIMIZE_CONVERSION_VALUE_OPT_IN: { label: 'Switch to Maximise conversion value bidding' },
  MAXIMIZE_CLICKS_OPT_IN: { label: 'Switch to Maximise clicks bidding' },
  ENHANCED_CPC_OPT_IN: { label: 'Turn on enhanced CPC' },

  // Budget
  CAMPAIGN_BUDGET: { label: 'Increase campaign budget' },
  FORECASTING_CAMPAIGN_BUDGET: { label: 'Increase campaign budget (forecast)' },
  MARGINAL_ROI_CAMPAIGN_BUDGET: { label: 'Increase budget on a high-return campaign' },
  MOVE_UNUSED_BUDGET: { label: 'Move unused budget' },

  // Reach and campaign type
  DISPLAY_EXPANSION_OPT_IN: { label: 'Turn on Display expansion' },
  SEARCH_PARTNERS_OPT_IN: { label: 'Turn on Search partners' },
  PERFORMANCE_MAX_OPT_IN: { label: 'Try Performance Max' },
  PERFORMANCE_MAX_FINAL_URL_OPT_IN: { label: 'Turn on final URL expansion' },

  // Measurement
  IMPROVE_GOOGLE_TAG_COVERAGE: { label: 'Improve Google tag coverage' },
};

const normalise = (type) => String(type ?? '').trim().toUpperCase();

/**
 * "IMPROVE_PERFORMANCE_MAX_AD_STRENGTH" → "Improve performance max ad strength".
 * Plain toLowerCase/toUpperCase, never the locale variants: the enum is ASCII
 * and a locale-aware case map (Turkish dotless i) would garble it.
 */
export function sentenceCaseEnum(type) {
  const words = normalise(type).replace(/_+/g, ' ').trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

/** The entry for a type, or null when this map does not know it. */
export function googleRecommendationType(type) {
  return GOOGLE_RECOMMENDATION_TYPES[normalise(type)] || null;
}

/** The headline for one recommendation of this type. Never the raw enum. */
export function googleRecommendationLabel(type) {
  return googleRecommendationType(type)?.label || sentenceCaseEnum(type) || 'Google recommendation';
}

/** The headline for `n` collapsed recommendations of one type. */
export function googleRecommendationGroupLabel(type, n) {
  const label = googleRecommendationLabel(type);
  if (!(n > 1)) return label;
  const many = googleRecommendationType(type)?.many;
  return many ? many.replace('{n}', String(n)) : `${label} (${n})`;
}

/** Whether this type is routine housekeeping that ranks below substantive cards. */
export function isMaintenanceRecommendationType(type) {
  return Boolean(googleRecommendationType(type)?.maintenance);
}

/** Names come only from Google's structured rows, matched by the full resource ID. */
export function verifiedCustomerMatchNames(recommendations = []) {
  const candidates = new Map();
  for (const row of Array.isArray(recommendations) ? recommendations : []) {
    if (row?.type !== 'REFRESH_CUSTOMER_MATCH_LIST'
        || typeof row.resource_name !== 'string'
        || !/^customers\/\d+\/recommendations\/[^/]+$/.test(row.resource_name)) continue;
    const name = row.details?.user_list_name;
    if (typeof name !== 'string' || !name.trim()) continue;
    if (!candidates.has(row.resource_name)) candidates.set(row.resource_name, new Set());
    candidates.get(row.resource_name).add(name.trim());
  }
  // Conflicting structured names are unavailable, not a reason to pick one.
  return new Map([...candidates].filter(([, names]) => names.size === 1)
    .map(([id, names]) => [id, [...names][0]]));
}

/** Describe the common action, without inventing a shared performance impact. */
export function googleRecommendationGroupSummary(type, items = []) {
  if (!items.length) return null;
  if (normalise(type) === 'REFRESH_CUSTOMER_MATCH_LIST') {
    const summary = 'Refresh stale customer-match lists to keep audience data current.';
    const names = items.map(item => item.listName);
    // Never imply a partial list of names is the complete set.
    const namesText = names.every(name => typeof name === 'string' && name.trim()) ? names.join(', ') : '';
    return items.length <= 3 && namesText && namesText.length <= 100
      ? `${namesText} — ${summary}` : summary;
  }
  if (normalise(type) === 'DYNAMIC_IMAGE_EXTENSION_OPT_IN') {
    return 'Review Google’s suggestion to add images from your landing pages.';
  }
  if (items.length === 1) return null; // Preserve a single item's own evidence.
  const reasons = [...new Set(items.map(item => item.reasoning).filter(Boolean))];
  if (reasons.length === 1 && items.every(item => item.reasoning)) return reasons[0];
  return `${googleRecommendationLabel(type)}. Review the individual recommendations for supporting evidence.`;
}
